import { useTranslation } from 'react-i18next'
import { Button } from '@/ui/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/components/common/Dialog'
import {
  useConsentStore,
  selectConsentCategories,
  selectIsPreferencesOpen,
} from '@/application/stores/consent.store'

/**
 * The "Customize" panel opened from the cookie banner (or reopened later from
 * the footer's "Cookies" link). Unlike the banner itself, this is a genuine
 * modal dialog: once someone has chosen to fine-tune categories, trapping
 * focus here is appropriate and expected — see CookieConsentBanner's
 * docstring for why the banner itself is deliberately not a trapping modal.
 *
 * Built on the same `DialogContent` used elsewhere in the app (focus trap,
 * Escape-to-close, portalling, scroll lock all come for free from Radix).
 */
export function CookiePreferencesDialog() {
  const { t } = useTranslation()
  const isOpen = useConsentStore(selectIsPreferencesOpen)
  const categories = useConsentStore(selectConsentCategories)
  const closePreferences = useConsentStore((state) => state.closePreferences)
  const setCategory = useConsentStore((state) => state.setCategory)
  const acceptAll = useConsentStore((state) => state.acceptAll)
  const refuseAll = useConsentStore((state) => state.refuseAll)
  const savePreferences = useConsentStore((state) => state.savePreferences)

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) closePreferences()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif">{t('cookies.preferences_title')}</DialogTitle>
          <DialogDescription>{t('cookies.preferences_description')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Strictly necessary — always on, not toggleable */}
          <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
            <div>
              <p className="text-sm font-medium">{t('cookies.category_necessary_title')}</p>
              <p className="text-sm text-muted-foreground">
                {t('cookies.category_necessary_description')}
              </p>
            </div>
            <span
              role="switch"
              aria-checked="true"
              aria-disabled="true"
              aria-label={t('cookies.category_necessary_title')}
              className="mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-primary opacity-60 cursor-not-allowed"
            >
              <span className="ml-6 inline-block h-4 w-4 rounded-full bg-primary-foreground" />
            </span>
          </div>

          {/* Third-party embedded media */}
          <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
            <div>
              <label htmlFor="consent-media-toggle" className="text-sm font-medium">
                {t('cookies.category_media_title')}
              </label>
              <p className="text-sm text-muted-foreground">
                {t('cookies.category_media_description')}
              </p>
            </div>
            <button
              id="consent-media-toggle"
              type="button"
              role="switch"
              aria-checked={categories.media}
              onClick={() => setCategory('media', !categories.media)}
              className={`mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                categories.media ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-background transition-transform ${
                  categories.media ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        <DialogFooter className="pt-2 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={refuseAll}
            className="h-auto min-h-10 py-2"
          >
            {t('cookies.refuse_all')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={savePreferences}
            className="h-auto min-h-10 py-2"
          >
            {t('cookies.save_preferences')}
          </Button>
          <Button type="button" onClick={acceptAll} className="h-auto min-h-10 py-2">
            {t('cookies.accept_all')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
